import { Duration, formatDuration, sub as subDuration } from 'date-fns';
import { queryOptions, useSuspenseQueries } from '@tanstack/react-query';
import {
    snapshotConfig,
    SnapshotInterface,
    SnapshotType,
} from 'app/agriculture/queries/snapshots/config/snapshotConfig';
import { useSelectedFarm } from 'app/agriculture/hooks/useSelectedFarm/useSelectedFarm';
import { bucketConfig } from 'app/agriculture/queries/snapshots/config/bucketConfig';
import { getSnapshotBucket } from 'app/agriculture/queries/snapshots/queryFns/getSnapshotBucket';
import { getSnapshotsLatestBefore } from 'app/agriculture/queries/snapshots/queryFns/getSnapshotsLatestBefore';
import { combineSnapshotQueryData } from 'app/agriculture/queries/snapshots/utils/combineSnapshotQueryData';
import { SnapshotsMap } from 'app/agriculture/queries/snapshots/types/SnapshotsMap';
import { Snapshot } from 'app/agriculture/queries/snapshots/types/Snapshot';
import { SnapshotBucketQueryKey } from 'app/agriculture/queries/snapshots/types/SnapshotBucketQueryKey';
import { getSnapshotBucketRefetchInterval } from 'app/agriculture/queries/snapshots/utils/getSnapshotBucketRefetchInterval';
import { useMemo } from 'react';
import { useDebounce } from 'use-debounce';
import { cursorTimeDebounceDelay } from 'app/agriculture/queries/snapshots/config/consts';
import { useTimelineViewMode } from 'app/agriculture/AgTimeline/context/TimelineContext';
import { TZDate } from '@date-fns/tz';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';

/**
 * returns a map of all the snapshots for each parent in every bucket required to cover a shelf life from cursor time
 * if shelf life is null, will only return a single buckets worth of snapshots along with a latest before for each parent
 * @param snapshotType
 * @param shelfLife
 * @param cursorTime
 */
export function useSnapshotQueryData<
    T extends SnapshotType,
    I extends Snapshot = SnapshotInterface<T>,
>({
    snapshotType,
    shelfLife,
    cursorTime: throttledCursorTime,
}: {
    snapshotType: T;
    shelfLife: Duration | null;
    cursorTime?: Date;
}): SnapshotsMap<I> {
    const { timeZone } = useTimeZone();
    const { bucketSize } = snapshotConfig[snapshotType];
    const { viewModesThatFetchAllBuckets } = bucketConfig[bucketSize];
    const { id: viewMode } = useTimelineViewMode();
    const [debouncedCursorTime] = useDebounce(throttledCursorTime, cursorTimeDebounceDelay);
    const cursorTime = viewModesThatFetchAllBuckets.includes(viewMode)
        ? throttledCursorTime
        : debouncedCursorTime;
    const farm = useSelectedFarm();
    const { eachBucketOfInterval, startOfBucket, bucketDuration } = bucketConfig[bucketSize];

    const cursorBucketStart = useMemo(
        () => startOfBucket(cursorTime ?? new TZDate(new Date(), timeZone)),
        [startOfBucket, cursorTime, timeZone],
    );

    // get a shelf life worth of buckets
    const bucketTimestamps = useMemo(() => {
        const cursorTimeOrNow = cursorTime ?? new TZDate(new Date(), timeZone);
        return eachBucketOfInterval({
            start: shelfLife == null ? cursorTimeOrNow : subDuration(cursorTimeOrNow, shelfLife),
            end: cursorTimeOrNow,
        });
    }, [
        cursorTime,
        // this is to catch new shelf life duration objects with the same value
        shelfLife == null ? shelfLife : formatDuration(shelfLife),
        eachBucketOfInterval,
        timeZone,
    ]);

    return useSuspenseQueries({
        queries: [
            ...bucketTimestamps.map((bucketTimestamp) =>
                queryOptions<SnapshotsMap<I>, Error, SnapshotsMap<I>, SnapshotBucketQueryKey<T>>({
                    queryKey: [snapshotType, { farmId: farm.id, bucketTimestamp }],
                    queryFn: getSnapshotBucket,
                    refetchInterval: getSnapshotBucketRefetchInterval(
                        bucketTimestamp,
                        bucketDuration,
                    ),
                }),
            ),
            ...(shelfLife == null
                ? [
                      queryOptions<
                          SnapshotsMap<I>,
                          Error,
                          SnapshotsMap<I>,
                          SnapshotBucketQueryKey<T>
                      >({
                          queryKey: [
                              snapshotType,
                              {
                                  farmId: farm.id,
                                  bucketTimestamp: cursorBucketStart,
                                  latestBefore: true,
                              },
                          ],
                          queryFn: getSnapshotsLatestBefore,
                      }),
                  ]
                : []),
        ],
        combine: combineSnapshotQueryData,
    });
}
